import React, { useEffect, useRef, useState } from "react";
import { MdCancel, MdOutlineCloudUpload } from "react-icons/md";
import {
  useCreateProjectMutation,
  useDeleteProjectMutation,
  useGetProjectByIdQuery,
  useUpdateProjectMutation,
} from "../../redux/projectApi";
import { useNavigate, useParams } from "react-router-dom";
import ButtonLoading from "../Button loading/ButtonLoading";
import { showConfirmDialog } from "../showConfirmDialog/ShowConfirmDialog";
import { toast } from "react-toastify";
import { showErrorToast } from "../../utils/Utils";

const AddProject = () => {
  const [title, setTitle] = useState("");
  const [image, setImage] = useState(null);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);
  const [titleError, setTitleError] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState("");

  const { id } = useParams();
  const isEditMode = Boolean(id);

  const { data, isLoading: isProjectLoading } = useGetProjectByIdQuery(id, {
    refetchOnFocus: false,
    skip: !isEditMode,
  });

  useEffect(() => {
    console.log(data);
    if (isEditMode && data) {
      setTitle(data?.project?.title);
      setDescription(data?.project?.content);

      if (data?.project?.coverImage?.imageUrl) {
        setImage({
          src: data?.project?.coverImage?.imageUrl,
          file: null,
        });
      }
    }
  }, [isEditMode, data]);

  const [createProject, { isLoading: isCreateLoading }] =
    useCreateProjectMutation();
  const [updateProject, { isLoading: isUpdateLoading }] =
    useUpdateProjectMutation();
  const [deleteProject, { isLoading: isDeleteLoading }] =
    useDeleteProjectMutation();
  const navigate = useNavigate();

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFile = (file) => {
    setError("");

    if (!file) {
      setError("No file selected.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError("Image size must be less than 10MB.");
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      // if (img.width !== 576 || img.height !== 774) {
      //   setError("Image dimensions must be exactly 576 x 774 pixels.");
      //   URL.revokeObjectURL(objectUrl);
      //   return;
      // }

      const newImage = {
        src: objectUrl,
        file,
      };

      setImage(newImage);
    };

    img.onerror = () => {
      setError("Invalid image file.");
      URL.revokeObjectURL(objectUrl);
    };

    img.src = objectUrl;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDelete = () => {
    setImage(null);
    setError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleTitleChange = (e) => {
    setTitle(e.target.value);
    setTitleError("");
  };
  const handleDescriptionChange = (e) => {
    setDescription(e.target.value);
    setDescriptionError("");
  };

  const validateForm = () => {
    let isValid = true;

    setTitleError("");
    setDescriptionError("");
    setError("");

    if (!title.trim()) {
      setTitleError("Title is required.");
      isValid = false;
    }

    if (!description.trim()) {
      setDescriptionError("Description is required.");
      isValid = false;
    } else {
      const wordCount = description.trim().split(/\s+/).length;
      if (wordCount < 1) {
        setDescriptionError("Description cannot be empty.");
        isValid = false;
      } else if (wordCount > 1000) {
        setDescriptionError("Description cannot exceed 1000 words.");
        isValid = false;
      }
    }

    if (!image) {
      setError("Please upload an image.");
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }
    const projectData = new FormData();
    projectData.append("title", title);
    projectData.append("content", description);
    projectData.append("coverImage", image?.file);

    try {
      let response;
      if (isEditMode) {
        response = await updateProject({
          id,
          projectData: projectData,
        }).unwrap();
      } else {
        response = await createProject(projectData).unwrap();
      }

      if (response.success) {
        navigate("/projects");
        toast.success(response.message);
      }
    } catch (err) {
      console.error("project error:", err);
      showErrorToast(err);
    }
    // console.log("Title:", title);
    // console.log("Description:", description);
    // console.log("Image File:", image.file);
  };

  const handleDeleteProject = async (id) => {
    try {
      const response = await deleteProject(id).unwrap();
      if (response.success) {
        toast.success(response.message);
        navigate("/projects");
      }
    } catch (err) {
      console.error("project error:", err);
      showErrorToast(err);
    }
  };

  const handleDeleteConfrim = (id) => {
    showConfirmDialog({
      title: "Delete Project",
      message: "Are you sure you want to delete this Project?",
      type: "danger",
      onConfirm: () => {
        handleDeleteProject(id);
      },
    });
  };

  return (
    <div className="flex flex-col gap-3 bg-white rounded-xl xl:p-8 lg:p-6 p-4 ">
      <div className="flex items-center justify-between">
        <h1 className="text-[#05004E] lg:text-[1.8rem] md:text-[1.4rem] text-[1.2rem] font-semibold">
          {isEditMode ? "Update Project" : "Add Project"}
        </h1>
      </div>
      {isProjectLoading ? (
        <div className="flex items-center lg:gap-4 md:gap-3 gap-2">
          <div className=" xl:w-[35%] md:w-[40%] w-full bg-[#efefef] animate-pulse h-[20rem] rounded-xl ">
            {" "}
          </div>
          <div className=" xl:w-[65%] md:w-[60%] w-full flex flex-col lg:gap-4 md:gap-3 gap-2">
            <div className="flex flex-col gap-1">
              <div className="h-6 w-40 bg-[#efefef] animate-pulse rounded-lg"></div>
              <div className=" w-full h-12 rounded-xl bg-[#efefef] animate-pulse"></div>
            </div>

            <div className="flex flex-col gap-1 ">
              <div className="h-6 w-40 bg-[#efefef] animate-pulse rounded-lg"></div>
              <div className=" w-full h-24 rounded-xl bg-[#efefef] animate-pulse"></div>
            </div>
          </div>
        </div>
      ) : (
        <form className="flex items-center md:flex-row flex-col lg:gap-4 md:gap-3 gap-2 lg:text-[1rem] text-[.9rem]">
          <div className="xl:w-[35%] md:w-[40%] w-full ">
            <div
              className="w-full xl:h-[21.5rem] lg:h-[18rem] h-[14rem] border border-[#00000033] lg:p-4 p-3 lg:rounded-xl rounded-lg relative "
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {!image && (
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer  flex flex-col md:gap-2 gap-1 items-center justify-center h-full w-full "
                >
                  <MdOutlineCloudUpload className="text-[#00643b] lg:size-10 md:size-8 size-6" />
                  <p className="text-center font-semibold text-[#0B0B0B] ">
                    Drag your file(s) or{" "}
                    <span className=" text-[#00643b]  ">browse</span>
                  </p>

                  <p className="text-[#6D6D6D] text-center lg:text-sm text-[12px]">
                    Max 10 MB files are allowed
                  </p>
                  <p className="text-[#6D6D6D] text-center lg:text-sm text-[12px]">
                    For best quality, use an image sized 576×774 pixels.
                  </p>
                </label>
              )}

              <input
                id="file-upload"
                type="file"
                accept="image/*"
                onChange={(e) => handleFile(e.target.files[0])}
                className="hidden"
                ref={fileInputRef}
              />

              {image && (
                <div className="absolute inset-0 flex justify-center items-center p-4">
                  <img
                    src={image.src}
                    alt="Upload Preview"
                    className=" object-cover w-full h-full md:rounded-[1rem] rounded-lg "
                  />
                  <button
                    onClick={() => handleDelete()}
                    className="absolute top-2 right-2 bg-white rounded-full  "
                  >
                    <MdCancel className="lg:size-8 size-6 text-red-500" />
                  </button>
                </div>
              )}
            </div>
            {error && (
              <p className="text-red-500 md:text-sm text-[12px] ">{error}</p>
            )}
          </div>
          <div className="xl:w-[65%] md:w-[60%] w-full flex flex-col lg:gap-4 md:gap-3 gap-2 ">
            <div className="flex flex-col md:gap-1 gap-[2px]">
              <label className="font-semibold">Add Title</label>
              <input
                type="text"
                placeholder="Enter Title"
                value={title}
                onChange={handleTitleChange}
                className="w-full border border-[#00000033] text-[#737791] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
              />
              {titleError && (
                <p className="text-red-500 md:text-sm text-[12px] ">
                  {titleError}
                </p>
              )}
            </div>
            <div className="flex flex-col md:gap-1 gap-[2px]">
              <label className="font-semibold">Add Description</label>
              <textarea
                rows="4"
                placeholder="Enter Description"
                value={description}
                onChange={handleDescriptionChange}
                disabled={
                  description.trim()
                    ? description.trim().split(/\s+/).length >= 1000
                    : false
                }
                className="w-full border border-[#00000033] text-[#737791] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
              ></textarea>
              <p className=" text-[#737791] text-sm font-medium">
                {description.trim()
                  ? description.trim().split(/\s+/).length
                  : 0}
                /1000 words
              </p>
              {descriptionError && (
                <p className="text-red-500 md:text-sm text-[12px] ">
                  {descriptionError}
                </p>
              )}
            </div>
          </div>
        </form>
      )}
      {isProjectLoading ? (
        <div className="flex lg:gap-4 md:gap-3 gap-2 items-center justify-end">
          <div className="md:w-[9rem] w-[4rem]   rounded-xl md:h-14 h-6 bg-[#efefef] animate-pulse"></div>
          <div className="md:w-[9rem] w-[4rem]   rounded-xl md:h-14 h-6 bg-[#efefef] animate-pulse"></div>
        </div>
      ) : (
        <div className="flex lg:gap-4 md:gap-3 gap-2 items-center justify-end">
          {data || isEditMode ? (
            <div
              disabled={isDeleteLoading}
              onClick={() => handleDeleteConfrim(data?.project?._id)}
              className={`lg:w-[10rem] md:w-[9rem] w-max md:py-2 md:px-0 py-1 px-2 md:text-[1rem] text-[.85rem]  border text-center border-[#D80000] flex items-center justify-center ${
                isDeleteLoading
                  ? "opacity-50 cursor-not-allowed"
                  : "opacity-100 cursor-pointer"
              } text-white bg-[#D80000] lg:rounded-xl md:rounded-lg rounded-md font-medium`}
            >
              {isDeleteLoading ? <ButtonLoading text=" Delete..." /> : "Delete"}
            </div>
          ) : (
            ""
          )}
          <button
            onClick={(e) => handleSubmit(e)}
            disabled={isCreateLoading || isUpdateLoading ? true : false}
            className="lg:w-[10rem] md:w-[9rem] w-max md:py-2 md:px-0 py-1 px-2 md:text-[1rem] text-[.85rem]  border border-[#00643B] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-white bg-[#00643B] lg:rounded-xl md:rounded-lg rounded-md font-medium"
          >
            {isEditMode ? (
              isUpdateLoading ? (
                <ButtonLoading text="Update..." />
              ) : (
                "Update"
              )
            ) : isCreateLoading ? (
              <ButtonLoading text="Add..." />
            ) : (
              "Add"
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default AddProject;
